import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer, PageTitle, CommonCss, Card } from 'styles/common.styles';
import { SEARCH_LABEL_W } from 'styles/theme';
import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import { showAlert } from 'utils/toastBridge';
import { isBdApiEnabled } from 'config/bdApiConfig';
import {
  YN_OPTIONS,
  createAcctUid,
  loadAcctClasses,
  saveAcctClasses,
  filterAcctClasses,
  validateAcctClasses,
  loadAcctUnits,
  saveAcctUnits,
  filterAcctUnits,
  validateAcctUnits,
} from 'budget/budgetAcctStore';


const Panel = styled(Card)`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  color: #1f2937;
  font-size: 0.875rem;
  font-weight: 700;
`;

const HeaderHint = styled.span`
  color: #6b7280;
  font-size: 0.72rem;
  font-weight: 400;
`;

const GridBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
`;

const Meta = styled.div`
  margin-bottom: 4px;
  flex-shrink: 0;
  color: #6b7280;
  font-size: 0.75rem;
`;

/**
 * 회계구분·회계단위 공통 CRUD 본체.
 * 두 축은 구조가 유사하지만 저장소와 비즈니스 검증은 axis별로 분리한다.
 */
const AccountingMasterRegisterInner = ({ axis }) => {
  const isClass = axis === 'class';
  const { user, isMaster } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const gridRef = useRef(null);

  const config = useMemo(
    () =>
      isClass
        ? {
            title: '회계구분등록',
            gridId: 'master-acct-class',
            storageKey: 'master-acct-class-register',
            codeField: 'acctClassCode',
            nameField: 'acctClassNm',
            uidField: 'acctClassUid',
            load: loadAcctClasses,
            save: saveAcctClasses,
            filter: filterAcctClasses,
            validate: validateAcctClasses,
            prefix: 'ACL',
            hint: '일반회계·특별회계·대행사업 등 편성 입력·조회 분류',
          }
        : {
            title: '회계단위등록',
            gridId: 'master-acct-unit',
            storageKey: 'master-acct-unit-register',
            codeField: 'acctUnitCode',
            nameField: 'acctUnitNm',
            uidField: 'acctUnitUid',
            load: loadAcctUnits,
            save: saveAcctUnits,
            filter: filterAcctUnits,
            validate: validateAcctUnits,
            prefix: 'AUN',
            hint: '본점·지점·센터 등 사업장·운영단위',
          },
    [isClass]
  );

  const searchDefaults = useMemo(
    () => ({
      tenantCode,
      [config.codeField]: '',
      [config.nameField]: '',
      useYn: 'Y',
    }),
    [tenantCode, config.codeField, config.nameField]
  );
  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues, setValue } = methods;

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [tenantCode, setValue]);

  const columns = useMemo(() => {
    const base = [
      hiCol.edTextColumnForKey({
        header: isClass ? '회계구분코드' : '회계단위코드',
        field: config.codeField,
        size: 130,
        required: true,
        title: '최초 저장 후에는 변경할 수 없습니다.',
      }),
      hiCol.edTextColumn({
        header: isClass ? '회계구분명' : '회계단위명',
        field: config.nameField,
        size: 180,
        required: true,
      }),
    ];
    if (isClass) {
      base.push(
        hiCol.edComboColumn({
          header: '기본',
          field: 'defaultYn',
          size: 80,
          options: YN_OPTIONS,
          title: '편성 화면 FormArea 최초 기본값 · 사용 중 1건 필수',
        })
      );
    }
    base.push(
      hiCol.edComboColumn({
        header: '사용',
        field: 'useYn',
        size: 80,
        options: YN_OPTIONS,
      }),
      hiCol.edNumberColumn({
        header: '정렬',
        field: 'sortOrder',
        size: 80,
        align: 'center',
      }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 260 })
    );
    return base;
  }, [isClass, config.codeField, config.nameField]);

  const grid = useHiGridHook({
    gridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: {
      [config.uidField]: '',
      [config.codeField]: '',
      [config.nameField]: '',
      ...(isClass ? { defaultYn: 'N' } : {}),
      useYn: 'Y',
      sortOrder: 10,
      remark: '',
    },
  });

  const handleSearch = useCallback(async () => {
    try {
      const rows = await config.load(tenantCode);
      gridRef.current?.setData?.(config.filter(rows, getValues()));
    } catch (e) {
      showAlert(e?.message || '조회에 실패했습니다.', 'error');
    }
  }, [config, tenantCode, getValues]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 최초 조회
  }, []);

  const handleAdd = useCallback(async () => {
    const rows = gridRef.current?.getData?.() || [];
    const maxSort = rows.reduce((max, r) => Math.max(max, Number(r.sortOrder) || 0), 0);
    const uid = createAcctUid(config.prefix);
    await grid.handleAdd({
      [config.uidField]: uid,
      id: uid,
      [config.codeField]: '',
      [config.nameField]: '',
      ...(isClass ? { defaultYn: 'N' } : {}),
      useYn: 'Y',
      sortOrder: Math.ceil((maxSort + 1) / 10) * 10,
      remark: '',
    });
  }, [grid, config, isClass]);

  const handleDelete = useCallback(async () => {
    await grid.handleDelete();
  }, [grid]);

  const handleSave = useCallback(async () => {
    try {
      const gridRows = gridRef.current?.getData?.() || [];
      const active = gridRows.filter((r) => r.mod !== 'D');
      const all = await config.load(tenantCode);
      const filteredUids = new Set(
        config.filter(all, getValues()).map((r) => r[config.uidField])
      );
      const outside = all.filter((r) => !filteredUids.has(r[config.uidField]));
      const next = [...outside, ...active];
      const validation = config.validate(tenantCode, next);
      if (!validation.ok) {
        showAlert(validation.message, 'warning');
        return;
      }

      // API: dirty(A/U/D)만 저장 · 로컬: 전체 merge
      let toSave = next;
      if (isBdApiEnabled()) {
        const dirtyFromGrid = gridRows.filter(
          (r) => r && (r.mod === 'A' || r.mod === 'U' || r.mod === 'D')
        );
        toSave = dirtyFromGrid;
      }
      const saved = await config.save(tenantCode, isBdApiEnabled() ? toSave : next);
      // API 저장 후 전체 재조회해 화면 동기화
      const refreshed = isBdApiEnabled() ? await config.load(tenantCode) : saved;
      gridRef.current?.setData?.(config.filter(refreshed, getValues()));
      showAlert('저장했습니다.', 'success');
    } catch (e) {
      showAlert(e?.message || '저장에 실패했습니다.', 'error');
    }
  }, [config, tenantCode, getValues]);

  const searchFields = useMemo(
    () => [
      [
        {
          name: config.codeField,
          label: isClass ? '구분코드' : '단위코드',
          type: 'text',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: config.nameField,
          label: isClass ? '구분명' : '단위명',
          type: 'text',
          width: CommonCss.w120,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'useYn',
          label: '사용',
          type: 'select',
          width: CommonCss.w70,
          labelWidth: SEARCH_LABEL_W,
          options: [{ value: '', label: '전체' }, ...YN_OPTIONS],
        },
      ],
    ],
    [config.codeField, config.nameField, isClass]
  );

  const getExportData = useCallback(
    () => gridRef.current?.getFilteredData?.() ?? gridRef.current?.getData?.() ?? [],
    []
  );
  const getExportCols = useCallback(() => gridRef.current?.getDataColumns?.() ?? [], []);
  useRegisterGridExport({
    id: config.gridId,
    label: config.title,
    getData: getExportData,
    getDataColumns: getExportCols,
    joinKeys: [config.uidField],
    order: 0,
  });
  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: config.title,
  });

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      { id: 'add', label: '추가', icon: '➕', onClick: handleAdd, hotkey: 'F8' },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleDelete,
        hotkey: 'F9',
        variant: 'danger',
      },
      {
        id: 'save',
        label: '저장',
        icon: '✅',
        onClick: handleSave,
        hotkey: 'F10',
        variant: 'success',
      },
      { id: 'excel', label: '엑셀', icon: '📥', onClick: openExcelDialog, hotkey: 'F11' },
    ],
    [handleSearch, handleAdd, handleDelete, handleSave, openExcelDialog]
  );

  if (!isMaster) {
    return (
      <PageContainer>
        <PageTitle>{config.title}</PageTitle>
        <Meta>기관 관리자 전용 화면입니다. 접근 권한이 없습니다.</Meta>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} />
        <CollapsibleSearchPanel
          storageKey={config.storageKey}
          prefsUserId={prefsUserId}
          summary={`${config.title} 조회`}
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={handleSearch}
            defaultValues={searchDefaults}
          />
        </CollapsibleSearchPanel>
      </FormProvider>

      <Meta>
        기관 {tenantCode}
        {' · '}
        {config.hint}
      </Meta>

      <Panel>
        <PanelHeader>
          <span>{config.title}</span>
          <HeaderHint>
            코드 최초 저장 후 변경 불가
            {isClass ? ' · 사용 중 기본 회계구분 1건 필수' : ''}
          </HeaderHint>
        </PanelHeader>
        <GridBody>
          <GridExportFocusScope gridId={config.gridId} style={{ flex: 1, minHeight: 0, height: '100%' }}>
            <HiGridComponent
              ref={gridRef}
              columns={columns}
              height="100%"
              showRowNumber
              rowNumberSize={56}
              showModColumn
              {...grid.contextMenuProps}
              enableHeaderMenu
              enableColumnFilters
              enableSelectionStats
              enableFreeze
              enableQuickExport
              exportFileName={config.title}
              gridId={config.gridId}
              prefsUserId={prefsUserId}
              colOptions={grid.colOptions}
            />
          </GridExportFocusScope>
        </GridBody>
      </Panel>

      {excelDialog}
    </PageContainer>
  );
};

const AccountingMasterRegister = ({ axis }) => (
  <GridExportProvider>
    <AccountingMasterRegisterInner axis={axis} />
  </GridExportProvider>
);

export default AccountingMasterRegister;

